/**
 * @Email: azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @JobTitle: Full Stack Developer
 * CustomUserChip Component
 *
 * Renders a customizable user chip with an optional avatar and delete action.
 * This component is typically used to display a user's name and optionally an avatar image.
 * It supports delete functionality, controlled appearance, and custom styling.
 *
 *
 * @param {React.ElementType} [Icon=PersonChip] - Icon component to use as a fallback avatar when no image is provided (Optional).
 * @param {string} id - The unique identifier for the chip element (Required).
 * @param {string} name - The name of the user to display in the chip (Required).
 * @param {function} [onDelete] - Callback function called when the delete icon is clicked (Optional).
 * @param {boolean} [showAvatar=false] - If true, shows the avatar (either provided via `avatarSrc` or fallback icon) (Optional).
 * @param {string} [avatarSrc=null] - URL string for the custom avatar image (Optional).
 * @param {boolean} [disabled=false] - If true, disables the delete functionality (Optional).
 * @param {string} [className=''] - Optional CSS class to apply to the chip (Optional).
 * @param {string} [classNameText=''] - Optional CSS class to apply to the chip text (Optional).
 * @param {string} classNameIcon - Optional CSS class applied to the icon inside the avatar.
 *                                  This allows custom styling of the fallback icon when no avatar image is provided.
 *                                  Default is 'icon-color-text-950'.
 * @param {string} classNameAvatar - Optional CSS class applied to the icon inside the avatar.
 *                                   fallback div avatar container or avatar image is provided.
 *                                   Default is 'icon-color-text-950'.
 * @param {object} [sx={}] - Additional MUI `sx` styles merged into the chip root styles (Optional).
 * @param {object} [avatarSx={}] - Additional MUI `sx` styles merged into the avatar styles (Optional).
 * @param {object} [textSx={}] - Additional MUI `sx` styles merged into the text styles (Optional).
 * @param {object} [iconStyle={}] - Additional inline styles applied to the fallback icon component (Optional).
 * @returns {JSX.Element} A styled chip component representing a user.
 */
import { Avatar, Box, Chip, Typography } from '@mui/material';
import closeIconSvg from '../assets/icon/closesvg.svg';

import { CustomUserChipStyle } from './Styles';
import { PersonChip } from './buttons/IconButtons';
import { POSITION_MODEL } from '../Helpers/PositionModel';

const CustomUserChip = ({
    Icon = PersonChip,
    id,
    name,
    onDelete,
    showAvatar = false,
    iconPosition = POSITION_MODEL.RIGHT,
    avatarSrc = null,
    disabled = false,
    className = '',
    variant = 'large',
    classNameText = 'ibl-font-w-500',
    deleteIcon = <img src={closeIconSvg} alt='closeSvg' />,
    classNameIcon='icon-color-text-950',
    classNameAvatar='',
    classNameBoxText='',
    sx = {},
    avatarSx = {},
    textSx = {},
    iconStyle = {},
    noWrap = false
}) => {

    const renderIcon = () => {
        return <>
            {showAvatar &&
            (avatarSrc ? (
                <Avatar
                    alt={name}
                    src={avatarSrc}
                    className={classNameAvatar}
                    sx={{ ...CustomUserChipStyle.avatar, ...avatarSx }}
                />
            ) : (
                <Avatar
                    sx={{ ...CustomUserChipStyle.avatar, ...avatarSx }}
                    className={classNameAvatar}
                >
                    <Icon
                        style={{ ...CustomUserChipStyle.icon, ...iconStyle }}
                        className={classNameIcon}
                    />
                </Avatar>
            ))}
        </>
    }

    return (
        <Chip
            id={id}
            variant='outlined'
            label={
                <Box sx={CustomUserChipStyle.labelContainer} className={classNameBoxText}>
                    {iconPosition === POSITION_MODEL.LEFT && renderIcon()}
                    {name && <Typography
                        component="span"
                        sx={{
                            ...(noWrap ? CustomUserChipStyle.nameTextNoWrap() : CustomUserChipStyle.nameText()),
                            ...textSx
                        }}
                        className={classNameText}>
                        {name}
                    </Typography>
                    }
                    {iconPosition === POSITION_MODEL.RIGHT && renderIcon()}
                </Box>
            }
            onDelete={disabled ? undefined : onDelete}
            deleteIcon={
                disabled ? undefined : <div className='mr-8px'>{deleteIcon}</div>
            }
            sx={
                variant === 'compact' ?
                    {
                        ...CustomUserChipStyle.chip(),
                        ...CustomUserChipStyle.compact,
                        ...sx
                    }
                :
                    {
                        ...CustomUserChipStyle.chip(),
                        ...sx
                    }
            }
            className={className}
        />
    );
};

export default CustomUserChip;
